import { useCallback, useEffect, useRef } from "react";
import { DisconnectReason, type Room } from "livekit-client";
import { getValidAccessTokenFromStorage } from "@weco/suite-client";
import { videoService } from "../api/video.api";
import { useCallSessionStore } from "../store/callSessionStore";
import { endReasonFor, type EvictionSignals } from "../lib/evictionTag";
import { rejoinDelayMs } from "../lib/rejoinBackoff";
import { tokenExpiry } from "./useGuestReconnect";
import { emitCallActivity } from "../lib/callActivity";

/** Call-recorder report of a rejoin step (#2223 J1); no effect on the rejoin. */
export const recordRejoin = (n: number, result: "started" | "ok" | "failed") =>
  emitCallActivity("recovery", { trigger: "livekit", step: `rejoin_${n}`, result });

/**
 * Auto-rejoin controller for the shell-hoisted <LiveKitRoom> (#1178, #2223).
 *
 * LiveKit's built-in reconnection (resume, then a full restart, ~45 s of
 * attempts) covers transient blips; once it gives up it fires
 * `onDisconnected`. This controller then treats a *terminal, involuntary*
 * disconnect as the start of an auto-rejoin.
 *
 * The first attempt reconnects at once with the LiveKit token already held
 * (`reconnectWithHeldCreds` → `useHeldCredsReconnect`), unless it expires
 * within a minute: the server sets no TTL (livekit-api's default is 6 h), and
 * every round trip before the new connection is more silence (#2223 AC24–26,
 * ≤ 2 s). Later attempts, or an expiring token, re-mint a fresh token
 * (name- and grant-preserving, guest via skipAuth) and swap it onto
 * <LiveKitRoom>, in case the held one is the problem.
 *
 * It never gives up while the session is active (#2223 AC13: "always brought
 * back, never left out" -- a bounded budget left 9 of 25 dropped participants
 * outside the call for good). Retries back off 2 s → 4 s → 8 s, then every
 * 10 s, and the browser coming back online or the page becoming visible
 * again skips the wait. Leaving stays possible at any time from the
 * reconnecting indicator. A deliberate leave (leaveIntent flag) or a
 * server-terminal reason (kick / room deleted / duplicate identity / server
 * shutdown) is NOT retried — those exit cleanly.
 */

// Belt-and-suspenders: if a token was minted and swapped in but LiveKitRoom
// emits neither onConnected nor onDisconnected, fail the attempt after this.
const WATCHDOG_MS = 15000;

// A held token this close to expiry is not reused: it could lapse mid-connect.
const HELD_TOKEN_MARGIN_MS = 60_000;

function heldTokenUsable(token: string): boolean {
  const exp = tokenExpiry(token);
  return exp !== null && exp * 1000 - Date.now() > HELD_TOKEN_MARGIN_MS;
}

// Server-terminal reasons — an auto-rejoin cannot help, so exit cleanly to the
// post-call screen rather than looping. DUPLICATE_IDENTITY and the tagged
// cross-room eviction get their own post-call copy (#1918).
const SUPPRESS_REASONS = new Set<DisconnectReason>([
  DisconnectReason.PARTICIPANT_REMOVED,
  DisconnectReason.ROOM_DELETED,
  DisconnectReason.DUPLICATE_IDENTITY,
  DisconnectReason.SERVER_SHUTDOWN,
]);

export interface CallReconnectController {
  /** Wire to <LiveKitRoom onDisconnected>. `signals` is what the session
   *  learned before the drop (the #1918 eviction marker + other call's name). */
  handleDisconnected: (reason?: DisconnectReason, signals?: EvictionSignals) => void;
  /** Wire to <LiveKitRoom onConnected>. */
  handleConnected: () => void;
  /**
   * Asks for a full rejoin while still connected (#2224: step 2 of the "not
   * heard" recovery). `disconnect` drops the current connection; that drop
   * then runs the same fresh-token rejoin as a network drop. It is not a
   * leave: leaveIntent stays unset, so the call report does not say "left".
   * Returns false when there is no session, a leave is under way, or a
   * rejoin is already running.
   */
  requestRejoin: (disconnect: () => unknown) => boolean;
}

export function useCallReconnect(): CallReconnectController {
  // Attempt currently in flight; 0 = connected normally / not reconnecting.
  // A ref (not state) so onDisconnected/onConnected read it synchronously.
  const attemptRef = useRef(0);
  const backoffTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const watchdog = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimers = useCallback(() => {
    if (backoffTimer.current) {
      clearTimeout(backoffTimer.current);
      backoffTimer.current = null;
    }
    if (watchdog.current) {
      clearTimeout(watchdog.current);
      watchdog.current = null;
    }
  }, []);

  // Forward declaration so runAttempt and fail can reference each other.
  const runAttemptRef = useRef<(n: number) => void>(() => {});

  // A single attempt failed (mint threw, no creds, watchdog, or the fresh
  // connection dropped again). Schedule the next one; there is no last one.
  const fail = useCallback(
    (n: number) => {
      // Stale signal (recovery or a later attempt already advanced us).
      if (attemptRef.current !== n) return;
      clearTimers();
      recordRejoin(n, "failed");
      const next = n + 1;
      attemptRef.current = next;
      const delay = rejoinDelayMs(next);
      backoffTimer.current = setTimeout(() => {
        backoffTimer.current = null;
        runAttemptRef.current(next);
      }, delay);
    },
    [clearTimers],
  );

  const runAttempt = useCallback(
    async (n: number) => {
      const { active } = useCallSessionStore.getState();
      if (!active) return; // session cleared (deliberate leave) — abandon.
      useCallSessionStore.getState().setReconnect("reconnecting", n);
      recordRejoin(n, "started");
      if (n === 1 && heldTokenUsable(active.token)) {
        // Same token string, so the token prop cannot drive the connect.
        useCallSessionStore.getState().reconnectWithHeldCreds();
        watchdog.current = setTimeout(() => fail(n), WATCHDOG_MS);
        return;
      }
      try {
        const authToken = await getValidAccessTokenFromStorage();
        const fresh = await videoService.getRoom(active.slug, {
          username: active.choices.username,
          skipAuth: !authToken,
        });
        // Bail if a deliberate leave / new join landed while we awaited.
        if (attemptRef.current !== n) return;
        if (!useCallSessionStore.getState().active) return;
        if (fresh.livekit?.url && fresh.livekit?.token) {
          // Swapping the token prop is what drives <LiveKitRoom> to open a
          // new connection; success → onConnected, failure → onDisconnected.
          useCallSessionStore
            .getState()
            .updateCreds(fresh.livekit.url, fresh.livekit.token);
          watchdog.current = setTimeout(() => fail(n), WATCHDOG_MS);
        } else {
          fail(n);
        }
      } catch {
        // Still offline / mint failed — count it as a failed attempt.
        fail(n);
      }
    },
    [fail],
  );
  runAttemptRef.current = runAttempt;

  // A fresh connection succeeded — cancel everything and return to idle.
  const recover = useCallback(() => {
    if (attemptRef.current > 0) recordRejoin(attemptRef.current, "ok");
    attemptRef.current = 0;
    clearTimers();
    useCallSessionStore.getState().setReconnect("idle", 0);
  }, [clearTimers]);

  const handleDisconnected = useCallback(
    (reason?: DisconnectReason, signals?: EvictionSignals) => {
      const store = useCallSessionStore.getState();
      // Deliberate leave (Leave/End/nav-away set the flag first) → clean exit.
      if (store.leaveIntent) {
        attemptRef.current = 0;
        clearTimers();
        // Read the room before clear() drops it. If this leave was the caller
        // abandoning a group ring nobody answered, everyone still ringing has
        // to stop (#1347 AC11); the request is a server-side no-op in every
        // other case, so it needs no group-call state here. A leave that takes
        // the page with it can lose this request — the participant_left
        // webhook is the backstop for exactly that.
        const roomId = store.active?.roomId;
        store.clear();
        if (roomId) {
          videoService.cancelGroupRing(roomId).catch(() => {});
        }
        return;
      }
      // Server-terminal reason → clean exit to post-call (no futile retry).
      // Same account took the call elsewhere → post-call says so (#1918).
      if (reason != null && SUPPRESS_REASONS.has(reason)) {
        attemptRef.current = 0;
        clearTimers();
        store.clear(endReasonFor(reason, signals));
        return;
      }
      // Involuntary network/signal drop.
      if (attemptRef.current === 0) {
        // First drop → start rejoining.
        attemptRef.current = 1;
        runAttempt(1);
      } else {
        // A reconnect attempt's connection dropped again → fail this attempt.
        fail(attemptRef.current);
      }
    },
    [clearTimers, fail, runAttempt],
  );

  const handleConnected = useCallback(() => {
    // Only meaningful mid-reconnect; the initial connect leaves attemptRef 0.
    if (attemptRef.current > 0) recover();
  }, [recover]);

  const requestRejoin = useCallback((disconnect: () => unknown) => {
    const store = useCallSessionStore.getState();
    if (!store.active || store.leaveIntent || attemptRef.current !== 0) return false;
    // Dropping the connection unpublishes our tracks while the room still
    // reads as connected, which LocalMediaStateSync would record as the user
    // turning them off -- and the rejoin would come back muted. Put back what
    // the user had once the drop is done (before the fresh token arrives).
    const { audioEnabled, videoEnabled } = store.active.choices;
    void Promise.resolve()
      .then(disconnect)
      .catch(() => {})
      .finally(() => {
        if (useCallSessionStore.getState().active) {
          useCallSessionStore.getState().setMediaEnabled(!!audioEnabled, !!videoEnabled);
        }
      });
    return true;
  }, []);

  // Network back or page visible again while waiting out a backoff: try now
  // rather than sit out up to 10 s of an already-working connection.
  useEffect(() => {
    const tryNow = () => {
      if (document.visibilityState !== "visible") return;
      if (attemptRef.current === 0 || !backoffTimer.current) return;
      clearTimeout(backoffTimer.current);
      backoffTimer.current = null;
      runAttemptRef.current(attemptRef.current);
    };
    window.addEventListener("online", tryNow);
    document.addEventListener("visibilitychange", tryNow);
    return () => {
      window.removeEventListener("online", tryNow);
      document.removeEventListener("visibilitychange", tryNow);
    };
  }, []);

  // Cancel any in-flight timers if the wrapper unmounts (session ended).
  useEffect(() => () => clearTimers(), [clearTimers]);

  return { handleDisconnected, handleConnected, requestRejoin };
}

/**
 * Mount inside <LiveKitRoom>: connects the room again with the creds it holds
 * whenever `reconnectWithHeldCreds` is called. Success or failure then reach
 * `handleConnected`/`handleDisconnected` through the room's own events, as a
 * token swap does.
 */
export function useHeldCredsReconnect(room: Room): void {
  const nonce = useCallSessionStore((s) => s.connectNonce);
  const seen = useRef(nonce);
  useEffect(() => {
    if (nonce === seen.current) return;
    seen.current = nonce;
    const active = useCallSessionStore.getState().active;
    if (active) void room.connect(active.url, active.token).catch(() => {});
  }, [nonce, room]);
}
